import { Box, CircularProgress, Divider, Grid, Typography } from '@mui/material';
import BenefitCard from './Components/BenefitCard';
import DetailCard from './Components/DetailCard';
import React, { useEffect, useState } from 'react';
import {
  deleteBeneficiaryAction,
  deleteNomineeAction,
  getBankDetails,
  getBeneficiaryNomineeDetails,
} from './APIs/action';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import ConfirmDeleteModal from './Components/DeleteModal';
import FirmDetails from './Components/FirmDetails';
import BankDetailCard from './Components/BankDetailCard';
import { InsuranceType } from './constants';
import { trackAnalyticsEvent } from '../../../utils/clevertap';
import { useDispatch, useSelector } from 'react-redux';

const BeneficiaryDetail = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const userData = useSelector(state => state.user?.userData);
	const [beneficiaryNomineeData, setBeneficiaryNomineeData] = useState({});
	const { benefit_card, selected_firm } = beneficiaryNomineeData;
	const insuranceType = location?.state?.insurance_type;
    const [loading, setLoading] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState({ beneficiary_id: null, isNominee: false, nominee_id: null });
	const selectedCompanyEntity = location?.state?.selectedCompanyEntity;
	
	// Track screen view
	useEffect(() => {
		trackAnalyticsEvent("BENEFICIARY_DETAIL_SCREEN_VIEW", {
			insurance_type: insuranceType,
			user_id: userData?.id
		});
	}, [insuranceType, userData?.id]);
	const handleDelete = (beneficiary_id, isNominee = false, nominee_id = null) => {
		// Track specific button click events based on type
		if (isNominee) {
			trackAnalyticsEvent("BENEFICIARY_DELETE_NOMINEE_BUTTON_CLICK", {
				insurance_type: insuranceType,
				beneficiary_id: beneficiary_id,
				nominee_id: nominee_id,
				user_id: userData?.id
			});
		} else {
			trackAnalyticsEvent("BENEFICIARY_DELETE_BENEFICIARY_BUTTON_CLICK", {
				insurance_type: insuranceType,
				beneficiary_id: beneficiary_id,
				user_id: userData?.id
			});
		}
		
		// Track delete action initiation
		trackAnalyticsEvent("BENEFICIARY_DELETE_ACTION_CLICK", {
			insurance_type: insuranceType,
			beneficiary_id: beneficiary_id,
			is_nominee: isNominee,
			nominee_id: nominee_id,
			user_id: userData?.id
		});
		
		setDeleteTarget({ beneficiary_id, isNominee, nominee_id, selectedCompanyEntity});
		setShowDeleteModal(true);
	};
	const confirmDelete = async () => {
		const { beneficiary_id, isNominee, nominee_id, selectedCompanyEntity } = deleteTarget;
		setLoading(true);
		
		// Track delete confirmation
		trackAnalyticsEvent("BENEFICIARY_DELETE_CONFIRM_CLICK", {
			insurance_type: insuranceType,
			beneficiary_id: beneficiary_id,
			is_nominee: isNominee,
			nominee_id: nominee_id,
			user_id: userData?.id
		});
		
		const response = isNominee
			? await deleteNomineeAction(beneficiary_id, nominee_id, insuranceType, selectedCompanyEntity)
			: await deleteBeneficiaryAction(beneficiary_id, insuranceType, selectedCompanyEntity);
		setLoading(false);
		setShowDeleteModal(false);

		const { error, message } = response;
		if (!error) {
			toast.success(message);
			
			// Track successful deletion
			trackAnalyticsEvent("BENEFICIARY_DELETE_SUCCESS", {
				insurance_type: insuranceType,
				beneficiary_id: beneficiary_id,
				is_nominee: isNominee,
				nominee_id: nominee_id,
				user_id: userData?.id
			});
			
			if(isNominee){
				navigate('/beneficiary/add-nominee', {
					state: {
						insurance_type: insuranceType,
						beneficiary_id: beneficiary_id,
						selectedCompanyEntity: selectedCompanyEntity
					},
				});
			}else{
				navigate('/beneficiary/add-beneficiary', {
					state: {
						insurance_type: insuranceType,
						selectedCompanyEntity: selectedCompanyEntity
					},
				});
			}
			
		} else {
			toast.error(message || 'Delete failed');
			
			// Track deletion error
			trackAnalyticsEvent("BENEFICIARY_DELETE_ERROR", {
				insurance_type: insuranceType,
				beneficiary_id: beneficiary_id,
				is_nominee: isNominee,
				nominee_id: nominee_id,
				error_message: message,
				user_id: userData?.id
			});
		}
	};
	const handleEdit = (item, isNominee = false, beneficiary_id = null) => {
		// Track edit action
		trackAnalyticsEvent("BENEFICIARY_EDIT_ACTION_CLICK", {
			insurance_type: insuranceType,
			beneficiary_id: beneficiary_id,
			is_nominee: isNominee,
			user_id: userData?.id
		});
		
		if(insuranceType !== InsuranceType.GAP_BENEFIT) {
			if (isNominee) {
				navigate('/beneficiary/add-nominee', {
					state: {
						insurance_type: insuranceType,
						nominee_data: item,
						beneficiary_id: beneficiary_id,
						isEdit: true,
						navigate_to: '/beneficiary/detail',
						selectedCompanyEntity: selectedCompanyEntity
					},
				});
			} else {
				navigate('/beneficiary/add-beneficiary', {
					state: {
						insurance_type: insuranceType,
						beneficiary_data: item,
						isEdit: true,
						navigate_to: '/beneficiary/detail',
						selectedCompanyEntity: selectedCompanyEntity
					},
				});
			}
		}else{
			navigate('/beneficiary/add-bank-details', {
				state: {
					insurance_type: insuranceType,
					account_data: item,
					isEdit: true,
					navigate_to: '/beneficiary/detail',
					selectedCompanyEntity: selectedCompanyEntity
				},
			});
		}
	};
	
	const getallDetails = async (insuranceType, selectedCompanyEntity) => {
		setLoader(true);
		let response;
		if(insuranceType === InsuranceType.GAP_BENEFIT) {
			response = await getBankDetails(insuranceType, selectedCompanyEntity);
		}else{
			response = await getBeneficiaryNomineeDetails(insuranceType, selectedCompanyEntity);
		}
		setLoader(false);
		const { data, error, message } = response;
		
		if (!error) {
			setBeneficiaryNomineeData(data);
		} else {
			toast.error(message)
		}   
	};
	useEffect(() => {
		if(insuranceType && selectedCompanyEntity){
			getallDetails(insuranceType, selectedCompanyEntity);
			dispatch({ type: 'RESET_BENEFICIARY_NOMINEE' });
			// dispatch({ type: 'RESET_NOMINEE_DATA' });
			// dispatch({ type: 'RESET_BENEFICIARY_DATA' });
		}else{
			navigate('/beneficiary');
		}
	}, [insuranceType, selectedCompanyEntity]);
	
	const [loader, setLoader] = useState(false);
    return (
        <Box sx={{ p: { xs:1.5, md:3 }, borderRadius: 2 }}>
			{!loader ? (
				<Grid container spacing={2} sx={{ alignItems: 'stretch', mb: 3 }}>
					<Grid item xs={12} sm={6}>
						<BenefitCard item={benefit_card} sx={{ height: '100%' }} />
					</Grid>
					<Grid item xs={12} sm={6}>
						<Box sx={{ background: '#fff', p: 2, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly' }}>
							{selected_firm && selected_firm.map((detail, i) => (
								<React.Fragment key={i}>
									<FirmDetails detail={detail} />
								</React.Fragment>
							))}
						</Box>
					</Grid>
					<Grid item xs={12} md={6}>
						<Box sx={{ 
							p: 2, background: '#fff', borderRadius: 2, height: '100%', p: { xs: 2, md: 2 } ,
							display: 'flex',
							flexDirection: 'column',
							gap: 2
						}}
					>
						{beneficiaryNomineeData?.details?.beneficiary_config && (
							<Box>
								<DetailCard 
									details={beneficiaryNomineeData?.details?.beneficiary_config} 
									handleDelete={handleDelete} 
									isNominee={false} 
									isDeletable={beneficiaryNomineeData?.details?.isDeletable} 
									selectedCompanyEntity={selectedCompanyEntity}
								/>
							</Box>
						)}
						{beneficiaryNomineeData?.details?.nominee_config && (
							<Box>
								<DetailCard 
									details={beneficiaryNomineeData?.details?.nominee_config} 
									isNominee={true}
									beneficiary_id={beneficiaryNomineeData?.details?.beneficiary_config.id} 
									selectedCompanyEntity={selectedCompanyEntity}
								/>
							</Box>
						)}
						{!beneficiaryNomineeData?.details?.isDeletable && (
							<Typography
								variant='body1'
								sx={{ color: '#666666' }}
							>
								*Above information cannot be edited or deleted.
							</Typography>
						)}
						{beneficiaryNomineeData?.bank_details && (
								<Box>
									<BankDetailCard 
										details={beneficiaryNomineeData?.bank_details} 
										handleDelete={handleDelete}  
										handleEdit={handleEdit} 
										isNominee={false} 
										selectedCompanyEntity={selectedCompanyEntity}
									/>
								</Box>
						)}
						</Box>
					</Grid>
				</Grid>
			):(
				<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3}}>
					<CircularProgress size={24} sx={{ color: '#FFA525' }} />
				</Box>
			)}
			<ConfirmDeleteModal
				open={showDeleteModal}
				onClose={() => setShowDeleteModal(false)}
				onConfirm={confirmDelete}
				loading={loading}
				isNominee={deleteTarget.isNominee}
			/>
        </Box>
    )
}

export default BeneficiaryDetail;
